import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { subscribeWithSelector } from 'zustand/middleware'
import { AppMessage, AppState, AppStore, Process } from './app.types'
import { asyncActions } from './app.async-actions'
import { createSelectors } from '../lib/utils/create-selectors'

const initialState: AppState = {
  locale: null,
  status: {
    loading: false,
    navExpanded: false,
    isSignUpVisible: false,
    isAdvancedEnabled: false,
  },
  processes: null,
  settings: null,
  auth: {
    user: null,
    isLoggedIn: false,
  },
}

const appStore = create(
  subscribeWithSelector(
    immer<AppStore>((set, get) => ({
      ...initialState,
      setLocale: (locale) =>
        set((state) => {
          state.locale = locale
        }),
      setLoading: (isLoading: boolean) =>
        set((state) => {
          state.status.loading = isLoading
        }),
      setIsAdvancedEnabled: (isEnabled) =>
        set((state) => {
          state.status.isAdvancedEnabled = isEnabled
        }),
      setNavExpanded: (expanded: boolean) =>
        set((state) => {
          state.status.navExpanded = expanded
        }),
      addProcess: (process: Process) =>
        set((state) => {
          if (!state.processes?.[process.id]) {
            state.processes = {
              ...get().processes,
              [process.id]: process,
            }
          }
        }),
      clearAllProcess: () =>
        set((state) => {
          state.processes = {}
        }),
      updateProcess: (processId: string, process: Partial<Process>) => {
        const current = get().processes?.[processId]

        set((state) => {
          if (current) {
            if (state.processes) {
              state.processes[processId] = {
                ...current,
                ...process,
              }
            } else {
              state.processes = {
                [processId]: process,
              }
            }
          }
        })
      },
      setMessage: (message: AppMessage | undefined, hideAfter = 3000) =>
        set((state) => {
          state.message = message
          if (hideAfter && state.message) {
            state.message.hideInterval = hideAfter
          }
        }),
      removeProcess: (processId: string) =>
        set((state) => {
          delete state.processes?.[processId]
        }),
      setSetting: (setting) =>
        set((state) => {
          state.settings = setting
        }),
      setUser: (user) =>
        set((state) => {
          state.auth.user = user
          state.auth.isLoggedIn = !!user?.accessToken && !!user?.refreshToken
        }),
      setSignupVisible: (isVisible) =>
        set((state) => {
          state.status.isSignUpVisible = isVisible
        }),
      setUserVerifyEmail: (email) => {
        set((state) => {
          state.auth.verifyEmail = email
        })
      },
      ...asyncActions(get),
    })),
  ),
)

export const useAppStore = createSelectors(appStore)
